import { createContext, useContext, useState, useRef, useCallback } from 'react'

/**
 * AIJobsContext — in-memory persistence for in-flight and completed AI requests.
 *
 * Why this exists: AI calls used to live in component-local state, so navigating
 * away from the triggering component lost the result even if the request finished.
 * This context hoists the state above the route boundary so a request started in
 * one component remains accessible (and continues running) regardless of unmounts.
 *
 * Scope: short-lived in-memory only. Jobs are cleared on full page reload.
 * For long-running jobs that need to survive reloads/closing the browser, use a
 * server-side job pattern (insert ai_jobs row, poll/subscribe). Currently that
 * pattern is reserved for policy-generate and policy-enrich.
 *
 * Usage:
 *   const { startJob, getJob } = useAIJobs()
 *   const job = getJob(jobKey) // { status, result, error, startedAt, completedAt } | undefined
 *
 *   const onClick = () => {
 *     startJob(jobKey, async () => {
 *       const res = await fetch('/api/ai/foo', { ... })
 *       if (!res.ok) throw new Error((await res.json()).error || 'Request failed')
 *       return await res.json()
 *     })
 *   }
 *
 * Job keys should be stable and unique per logical operation, e.g.:
 *   `followup-consolidate:${assessmentId}`
 *   `cdm-classify:${techName}:${vendor}`
 *   `bia-estimate:${systemId}`
 *
 * Calling startJob with a key that already has a running job is a no-op (dedupe).
 * Calling startJob with a key that has a completed/errored job overwrites it.
 */

const AIJobsContext = createContext(null)

export function AIJobsProvider({ children }) {
  // jobs: { [key]: { status, result, error, startedAt, completedAt } }
  const [jobs, setJobs] = useState({})
  // In-flight promises kept in a ref so they survive re-renders without
  // triggering them. Used to dedupe concurrent startJob calls with the same key.
  const inFlightRef = useRef({})

  const updateJob = useCallback((key, patch) => {
    setJobs(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }))
  }, [])

  const startJob = useCallback((key, fetchFn) => {
    if (!key || typeof fetchFn !== 'function') {
      console.warn('[AIJobs] startJob requires a key and a fetchFn')
      return Promise.reject(new Error('Invalid startJob call'))
    }

    // Dedupe: if a job with this key is already running, return its promise
    if (inFlightRef.current[key]) {
      return inFlightRef.current[key]
    }

    setJobs(prev => ({
      ...prev,
      [key]: {
        status: 'running',
        result: null,
        error: null,
        startedAt: new Date().toISOString(),
        completedAt: null,
      },
    }))

    const promise = (async () => {
      try {
        const result = await fetchFn()
        updateJob(key, {
          status: 'success',
          result,
          error: null,
          completedAt: new Date().toISOString(),
        })
        return result
      } catch (err) {
        updateJob(key, {
          status: 'error',
          result: null,
          error: err?.message || String(err),
          completedAt: new Date().toISOString(),
        })
        throw err
      } finally {
        delete inFlightRef.current[key]
      }
    })()

    inFlightRef.current[key] = promise
    return promise
  }, [updateJob])

  const getJob = useCallback((key) => {
    if (!key) return undefined
    return jobs[key]
  }, [jobs])

  const clearJob = useCallback((key) => {
    setJobs(prev => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  return (
    <AIJobsContext.Provider value={{ startJob, getJob, clearJob }}>
      {children}
    </AIJobsContext.Provider>
  )
}

export function useAIJobs() {
  const ctx = useContext(AIJobsContext)
  if (!ctx) {
    throw new Error('useAIJobs must be used within an AIJobsProvider')
  }
  return ctx
}
